import React, { createContext, useContext, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import { useUserContext } from '@ion/user';

import { usePipelinePageContext } from 'app/pipeline-details/PipelinePageContextProvider';
import resolvePipelineConfiguration from 'app/shared/translation/pipelines/resolvePipelineConfiguration';

import RemoveDeployedIntegrationsModal from './PipelineIntegrations/RemoveDeployedIntegrationsModal';
import PipelineVarsModal from './PipelineIntegrations/PipelineVarsModal';
import DeployModal from './PipelineIntegrations/DeployModal';

const PipelineIntegrationTableContext = createContext(null);

export default function PipelineIntegrationTableContextProvider({ children }) {
  const { pipeline, refetch } = usePipelinePageContext();
  const { user } = useUserContext();
  const [deployModalOpen, setDeployModalOpen] = useState(false);
  const [loadingDiff, setLoadingDiff] = useState(false);
  const [pipelineVariablesModalOpen, setPipelineVariablesModalOpen] = useState(false);
  const [removeDeployedIntegrationsModalOpen, setRemoveDeployedIntegrationsModalOpen] = useState(false);

  const { deployedPipeline, filter, transform, eventSplit } = pipeline;
  const pipelineConfiguration = useMemo(
    () =>
      resolvePipelineConfiguration({
        deployedPipeline,
        configuredPipeline: { filter, transform, eventSplit },
      }),
    [deployedPipeline, filter, transform, eventSplit]
  );

  const openPipelineDiffModal = () => {
    setLoadingDiff(true);
    setDeployModalOpen(true);
  };

  return (
    <PipelineIntegrationTableContext.Provider
      value={{
        setRemoveDeployedIntegrationsModalOpen,
        setPipelineVariablesModalOpen,
        loadingDiff,
        openPipelineDiffModal,
      }}
    >
      {children}
      {removeDeployedIntegrationsModalOpen && (
        <RemoveDeployedIntegrationsModal
          setRemoveDeployedIntegrationsModalOpen={setRemoveDeployedIntegrationsModalOpen}
          refetch={refetch}
        />
      )}
      {pipelineVariablesModalOpen && (
        <PipelineVarsModal pipeline={pipeline} setPipelineVariablesModalOpen={setPipelineVariablesModalOpen} />
      )}
      <DeployModal
        deployModalOpen={deployModalOpen}
        setDeployModalOpen={setDeployModalOpen}
        clusterId={pipeline.cluster.id}
        writekey={pipeline.writekey}
        pipelineId={pipeline.id}
        pipelineName={pipeline.name}
        author={user.email}
        pipelineConfiguration={pipelineConfiguration}
        setLoadingDiff={setLoadingDiff}
        deployedRevisions={pipeline.deployedRevisions}
        pipelineIntegrations={pipeline.pipelineIntegrations}
        cliManagedIntegrations={pipeline.cliManagedIntegrationJson}
        refetch={refetch}
      />
    </PipelineIntegrationTableContext.Provider>
  );
}

export function usePipelineIntegrationTableContext() {
  return useContext(PipelineIntegrationTableContext);
}

PipelineIntegrationTableContextProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
